import * as React from "react";
import { Crosshair, Plus, Save, Trash2 } from "lucide-react";
import { useSpaceStore } from "../../stores/spaceStore";
import { useSpacesApi } from "../../../../../common/hooks/use-spaces-api.hook";
import { Button } from "../ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/Card";
import { Checkbox } from "../ui/Checkbox";
import { Input } from "../ui/Input";
import { useToast } from "../ui/Toast";
import { cn } from "../../lib/utils";
import type { SurfaceType, SurfaceZone } from "../../types";

const KINDS: SurfaceType[] = ["floor", "walls", "ceiling", "stairs"];
const KIND_LABEL: Record<SurfaceType, string> = { floor: "Floor", walls: "Walls", ceiling: "Ceiling", stairs: "Stairs" };

/** A zone being edited: the marker is optional until saved (it defaults to the members' centre). */
type DraftZone = Omit<SurfaceZone, "marker"> & { marker?: { x: number; y: number } };

interface SurfacePoint { key: string; kind: SurfaceType; x: number; y: number }

const newId = () =>
  (typeof crypto !== "undefined" && "randomUUID" in crypto)
    ? crypto.randomUUID()
    : `z${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/**
 * Group surfaces under one visualizer marker. Zones are saved straight to
 * pms-api (not with Deploy), so grouping can be changed without re-uploading
 * the space's masks and geometry. Surfaces left out of every zone keep their
 * own marker in the visualizer.
 */
export function ZoneEditor() {
  const { spaceInfo, setSpaceInfo, originalImage } = useSpaceStore();
  const { updateZones } = useSpacesApi();
  const { addToast } = useToast();

  const [zones, setZones] = React.useState<DraftZone[]>([]);
  const [placingId, setPlacingId] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  // Reload the drafts whenever another space is loaded.
  React.useEffect(() => {
    setZones((spaceInfo?.zones || []).map((z) => ({ ...z })));
    setPlacingId(null);
    setDirty(false);
  }, [spaceInfo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every surface marker, in the space's stored pixel space.
  const points: SurfacePoint[] = React.useMemo(() => {
    const out: SurfacePoint[] = [];
    for (const kind of KINDS) {
      for (const m of spaceInfo?.surfaceMarkers?.[kind] || []) {
        if (m && m.id != null) out.push({ key: `${kind}_${m.id}`, kind, x: m.x, y: m.y });
      }
    }
    return out;
  }, [spaceInfo]);

  const owner = React.useMemo(() => {
    const map = new Map<string, string>();
    zones.forEach((z) => z.surfaces.forEach((s) => map.set(s, z.id)));
    return map;
  }, [zones]);

  if (!spaceInfo) return null;
  const width = spaceInfo.width || 1;
  const height = spaceInfo.height || 1;

  const edit = (id: string, patch: Partial<DraftZone>) => {
    setZones((prev) => prev.map((z) => (z.id === id ? { ...z, ...patch } : z)));
    setDirty(true);
  };

  const addZone = () => {
    setZones((prev) => [...prev, { id: newId(), kind: "walls", label: "", surfaces: [] }]);
    setDirty(true);
  };

  const removeZone = (id: string) => {
    setZones((prev) => prev.filter((z) => z.id !== id));
    if (placingId === id) setPlacingId(null);
    setDirty(true);
  };

  const toggleMember = (zone: DraftZone, key: string) => {
    const surfaces = zone.surfaces.includes(key)
      ? zone.surfaces.filter((s) => s !== key)
      : [...zone.surfaces, key];
    edit(zone.id, { surfaces });
  };

  const centreOf = (keys: string[]) => {
    const pts = points.filter((p) => keys.includes(p.key));
    if (!pts.length) return null;
    return {
      x: Math.round(pts.reduce((a, p) => a + p.x, 0) / pts.length),
      y: Math.round(pts.reduce((a, p) => a + p.y, 0) / pts.length),
    };
  };

  const markerOf = (zone: DraftZone) => zone.marker ?? centreOf(zone.surfaces);

  const onImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!placingId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * width);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * height);
    edit(placingId, { marker: { x, y } });
    setPlacingId(null);
  };

  const problems = zones.flatMap((z, i) => {
    const name = z.label || `Zone ${i + 1}`;
    if (!z.surfaces.length) return [`${name} has no surfaces`];
    return [];
  });

  const save = async () => {
    if (problems.length) {
      addToast("error", problems[0]);
      return;
    }
    const payload: SurfaceZone[] = zones.map((z) => ({
      id: z.id,
      kind: z.kind,
      ...(z.label?.trim() ? { label: z.label.trim() } : {}),
      marker: markerOf(z)!,
      surfaces: z.surfaces,
    }));
    setSaving(true);
    try {
      await updateZones(spaceInfo.id, payload);
      setSpaceInfo({ ...spaceInfo, zones: payload });
      setZones(payload.map((z) => ({ ...z })));
      setDirty(false);
      addToast("success", payload.length ? `Saved ${payload.length} zone(s)` : "Zones removed");
    } catch (err: any) {
      let message = "Could not save zones";
      try {
        const body = await err?.json?.();
        if (body?.message) message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
      } catch { /* not a JSON error */ }
      addToast("error", message);
    } finally {
      setSaving(false);
    }
  };

  const pct = (v: number, of: number) => `${(v / of) * 100}%`;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Marker zones</CardTitle>
        <CardDescription>
          Group surfaces under one marker in the visualizer: picking the marker maps the product on every
          surface of the group. Surfaces not in a zone keep their own marker. Saved immediately, no deploy needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)]">
        {/* Preview: surface markers as dots, zone markers as labelled pins */}
        <div className="relative select-none">
          {originalImage ? (
            <img
              src={originalImage}
              alt="Space"
              onClick={onImageClick}
              className={cn("block w-full h-auto rounded-md", placingId && "cursor-crosshair")}
            />
          ) : (
            <div className="aspect-video rounded-md bg-muted" />
          )}
          {points.map((p) => {
            const zoneId = owner.get(p.key);
            return (
              <div
                key={p.key}
                title={p.key}
                className={cn(
                  "pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded px-1 text-[10px] font-medium",
                  zoneId ? "bg-primary/80 text-primary-foreground" : "bg-black/60 text-white",
                )}
                style={{ left: pct(p.x, width), top: pct(p.y, height) }}
              >
                {p.key}
              </div>
            );
          })}
          {zones.map((z, i) => {
            const m = markerOf(z);
            if (!m) return null;
            return (
              <div
                key={z.id}
                className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground shadow"
                style={{ left: pct(m.x, width), top: pct(m.y, height) }}
              >
                {z.label || `Zone ${i + 1}`}
              </div>
            );
          })}
          {placingId && (
            <div className="absolute left-2 top-2 rounded bg-black/70 px-2 py-1 text-xs text-white">
              Click the image to place the marker
            </div>
          )}
        </div>

        {/* Zone list */}
        <div className="space-y-3">
          {zones.length === 0 && (
            <p className="text-sm text-muted-foreground">No zones: every surface has its own marker.</p>
          )}
          {zones.map((zone, i) => {
            const candidates = points.filter((p) => p.kind === zone.kind);
            return (
              <div key={zone.id} className="space-y-3 rounded-md border p-3">
                <div className="flex items-center gap-2">
                  <Input
                    value={zone.label || ""}
                    placeholder={`Zone ${i + 1} (shown on the marker)`}
                    maxLength={60}
                    onChange={(e) => edit(zone.id, { label: e.target.value })}
                  />
                  <select
                    value={zone.kind}
                    onChange={(e) => edit(zone.id, { kind: e.target.value as SurfaceType, surfaces: [], marker: undefined })}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                  </select>
                  <Button variant="destructive" size="icon" onClick={() => removeZone(zone.id)} title="Delete zone">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {candidates.length === 0 && (
                    <span className="text-xs text-muted-foreground">This space has no {KIND_LABEL[zone.kind].toLowerCase()} surfaces.</span>
                  )}
                  {candidates.map((p) => {
                    const takenBy = owner.get(p.key);
                    const taken = !!takenBy && takenBy !== zone.id;
                    return (
                      <Checkbox
                        key={p.key}
                        label={p.key}
                        checked={zone.surfaces.includes(p.key)}
                        disabled={taken}
                        title={taken ? "Already in another zone" : undefined}
                        onCheckedChange={() => toggleMember(zone, p.key)}
                      />
                    );
                  })}
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Button
                    variant={placingId === zone.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPlacingId(placingId === zone.id ? null : zone.id)}
                  >
                    <Crosshair className="mr-1 h-3 w-3" />
                    {placingId === zone.id ? "Cancel" : "Place marker"}
                  </Button>
                  {zone.marker
                    ? <span>at {zone.marker.x}, {zone.marker.y} · <button className="underline" onClick={() => edit(zone.id, { marker: undefined })}>reset to centre</button></span>
                    : <span>centre of its surfaces</span>}
                </div>
              </div>
            );
          })}

          <div className="flex gap-2">
            <Button variant="outline" onClick={addZone}>
              <Plus className="mr-1 h-4 w-4" /> Add zone
            </Button>
            <Button onClick={save} disabled={saving || !dirty}>
              <Save className="mr-1 h-4 w-4" /> {saving ? "Saving…" : "Save zones"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
